/**
 * AML (Anti-Money Laundering) Types
 *
 * Type definitions for the AML detection system with two-person review workflow.
 * Based on AML_UI_UX_DESIGN.md v2.2 specification.
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * AML Issue Status - New workflow with needs_info state
 * Aligned with backend AMLIssueStatus enum
 */
export type AMLIssueStatus =
  | 'new'               // Awaiting first review
  | 'needs_info'        // Additional information requested
  | 'second_review'     // First reviewer proposed outcome, awaiting second review
  | 'closed';           // Final: Closed with outcome (no_concern or escalated_action_taken)

/**
 * AML Issue Types (K-U)
 * Continuation from Compliance types (A-J)
 */
export type AMLIssueType = 'K' | 'L' | 'M' | 'N' | 'O' | 'P' | 'Q' | 'R' | 'S' | 'T' | 'U';

/**
 * AML Issue Type Display Information
 */
export const AML_ISSUE_TYPE_INFO: Record<AMLIssueType, { name: string; description: string }> = {
  K: { name: 'New Counterparty', description: 'First-time supplier or customer with large transaction' },
  L: { name: 'Contact Spikes', description: 'Unusual increase in supplier/customer count' },
  M: { name: 'Soft GL Usage', description: 'Large transactions in suspense/clearing accounts' },
  N: { name: 'Missing Attachments', description: 'High-value transactions without supporting documents' },
  O: { name: 'Volume Spikes', description: 'Unusual increase in spend/revenue volume' },
  P: { name: 'Structuring', description: 'Multiple similar-amount transactions within 72h suggesting intentional splitting' },
  Q: { name: 'Pass-Through', description: 'Money in then out within 7 days with similar amounts (above threshold)' },
  R: { name: 'High Credit Note Ratio', description: 'Contacts with excessive credit notes (>15%)' },
  S: { name: 'Round Numbers', description: 'Suspiciously round-number invoices or duplicate references' },
  T: { name: 'Backdated', description: 'Transactions entered more than 3 days after their date' },
  U: { name: 'Shared Bank', description: 'Multiple suppliers receiving payments to the same bank account' },
};

/**
 * AML Severity Levels
 */
export type AMLSeverity = 'HIGH' | 'MEDIUM' | 'LOW';

/**
 * Proposed Outcome for First Review
 */
export type ProposedOutcome = 'no_concern' | 'escalate';

/**
 * Final Outcome for Closed Issues
 */
export type AMLOutcome = 'no_concern' | 'escalated_action_taken';

/**
 * Second Review Decision
 */
export type SecondReviewDecision = 'agree' | 'needs_info';

/**
 * Review Note Action Types
 */
export type ReviewNoteAction =
  | 'claim'
  | 'release'
  | 'first_review'
  | 'second_review'
  | 'add_info'
  | 'force_close'
  | 'comment'
  | 'snooze'
  | 'unsnooze';

// ============================================================================
// CORE INTERFACES
// ============================================================================

/**
 * Review Note - Individual entry in the audit trail (IMMUTABLE)
 */
export interface ReviewNote {
  note_id: string;
  user_id: string;
  user_name: string;
  timestamp: string; // ISO datetime
  action: ReviewNoteAction;
  content: string;
  proposed_outcome?: ProposedOutcome;
  metadata?: Record<string, unknown>;
}

/**
 * Line Item - Individual line within a transaction
 */
export interface AMLLineItem {
  line_item_id: string;
  line_index: number;
  description: string;
  unit_amount: number;
  quantity: number;
  line_amount: number;
  account_code?: string;
  account_name?: string;
  formatted_account?: string;
  tax_type?: string;
  tax_amount?: number;
  formatted_tax?: string;
  vat_rate_name?: string;  // Tax name like "20% VAT" (matches Compliance pattern)
}

/**
 * Transaction within a Volume Contributor (Type O)
 */
export interface ContactTransaction {
  transaction_id: string;
  date: string;
  amount: number;
  reference: string;
  description: string;
}

/**
 * Volume Contributor (Type O: Volume Spike)
 * Individual contact contributing to volume spike
 */
export interface VolumeContributor {
  contact_id: string;
  contact_name: string;
  baseline_amount: number;        // Average monthly amount in baseline period
  current_amount: number;         // This month's spend/revenue
  variance_percent: number | null; // Percentage increase (null if new contact)
  variance_amount: number;        // Absolute increase
  variance_direction: 'increase' | 'decrease' | 'new';
  transaction_count: number;
  is_new: boolean;               // Was this contact in baseline period?
  baseline_months: number;       // Data quality indicator
  transactions: ContactTransaction[]; // Sample transactions (up to 10)
}

/**
 * Count Spike Contact (Type L: Count Spike)
 * Individual contact contributing to count spike (new suppliers/customers)
 */
export interface CountSpikeContact {
  contact_id: string;
  contact_name: string;
  transaction_count: number;
  total_amount: number;
  is_new: boolean;
  transactions?: ContactTransaction[];
}

/**
 * Contributor Summary (Type O: Volume Spike)
 * Aggregate statistics for volume spike contributors
 */
export interface ContributorSummary {
  total_contacts: number;
  contacts_with_increase: number;
  contacts_with_decrease: number;
  new_contacts_count: number;
  new_contacts_amount: number;
}

/**
 * Baseline Metadata (Type L & O: AML Baseline System)
 * Enrichment data showing which baseline was used for detection
 */
export interface BaselineMetadata {
  baseline_source: 'approved' | 'statistical';  // Which baseline was used
  approved_baseline: number | null;              // Approved value if exists
  rolling_average: number;                       // 6-month statistical average
  effective_baseline: number;                    // MAX(approved, rolling) - actual threshold used
  is_baseline_expired: boolean;                  // Whether approved baseline expired
  is_baseline_converged: boolean;                // Whether rolling avg exceeded approved by 20%+
  approved_at?: string;                          // ISO datetime when baseline was approved (optional)
}

/**
 * AML Issue Metadata - Transaction details similar to Compliance
 */
export interface AMLIssueMetadata {
  // Document identification
  document_type?: 'BILL' | 'INVOICE';
  document_number?: string;
  contact_name?: string;

  // Transaction identification
  transaction_type?: 'ACCPAY' | 'ACCREC' | 'SPEND' | 'RECEIVE';
  transaction_date?: string;
  reference?: string;
  total_amount?: number;

  // Dates
  date_issued?: string;
  date_due?: string;

  // Account information
  account_code?: string;
  account_name?: string;
  description?: string;

  // Line items detail
  line_items?: AMLLineItem[];

  // Attachments
  has_attachments?: boolean;

  // Type O: Volume Spike - Contact breakdown
  contacts?: VolumeContributor[] | CountSpikeContact[];
  contributor_summary?: ContributorSummary;

  // Type L: Count Spike metadata fields
  baseline_contact_count?: number;
  current_contact_count?: number;
  new_contacts_count?: number;
  period_total_amount?: number;

  // Baseline metadata (Type L & O)
  baseline_metadata?: BaselineMetadata;

  // Additional metadata
  [key: string]: any;
}

/**
 * AML Issue - Main issue model with two-person workflow fields
 */
export interface AMLIssue {
  // === CORE IDENTIFIERS ===
  issue_hash: string;           // Document ID (SHA-256)
  entity_id: string;            // Entity UUID
  issue_type: AMLIssueType;     // K, L, M, N, O, R, S, T

  // === ISSUE DETAILS ===
  severity: AMLSeverity;
  status: AMLIssueStatus;
  title: string;
  description: string;
  detected_at: string;          // ISO datetime
  source_transactions: string[]; // Transaction IDs (array for bulk patterns)
  total_amount: number;
  currency_code: string;

  // === SINGLE TRANSACTION LINK (NEW) ===
  source_id?: string;                      // Single transaction ID for Xero linking
  source_transaction_reference?: string;   // Invoice/bill/bank transaction reference number

  // === RICH METADATA (NEW) ===
  metadata?: AMLIssueMetadata;             // Transaction details, line items, accounts

  // === CONTACT/COUNTERPARTY ===
  counterparty_id?: string;
  counterparty_name?: string;

  // === RISK ASSESSMENT ===
  risk_score: number;           // 0-100
  confidence_score: number;     // 0.0-1.0
  data_quality_score: number;   // 0.0-1.0
  missing_data_fields: string[];

  // === CLAIMING (prevents race conditions) ===
  claimed_by?: string;          // User ID who claimed
  claimed_at?: string;          // ISO datetime
  claim_expires_at?: string;    // Auto-release after 24 hours

  // === FIRST REVIEW ===
  first_reviewer_id?: string;
  first_reviewer_name?: string;
  first_reviewed_at?: string;   // ISO datetime
  proposed_outcome?: ProposedOutcome;
  proposed_reason?: string;

  // === SECOND REVIEW ===
  second_reviewer_id?: string;
  second_reviewer_name?: string;
  second_reviewed_at?: string;  // ISO datetime
  final_outcome?: AMLOutcome;
  final_reason?: string;

  // === NEEDS INFO TRACKING ===
  needs_info_count?: number;    // How many times info was requested
  needs_info_reason?: string;   // Reason for requesting more info

  // === AUDIT TRAIL ===
  review_notes: ReviewNote[];

  // === OPTIMISTIC LOCKING ===
  version: number;

  // === ESCALATION ===
  escalated_at?: string;
  escalated_to?: string;
  escalation_reason?: string;

  // === SNOOZE FIELDS ===
  snoozed_until?: string;       // ISO datetime, max 90 days
  snoozed_by?: string;
  snoozed_at?: string;
  is_currently_snoozed: boolean;

  // === DATE FIELDS FOR FILTERING ===
  source_transaction_date: string; // YYYY-MM-DD for efficient queries

  // === SAR FIELDS ===
  sar_filed: boolean;
  sar_reference?: string;
  sar_filed_at?: string;
  sar_filed_by?: string;
}

// ============================================================================
// API REQUEST/RESPONSE TYPES
// ============================================================================

/**
 * Query parameters for listing AML issues
 */
export interface AMLIssuesQueryParams {
  issue_hash?: string;          // For deep linking - fetch single issue
  status?: AMLIssueStatus[];
  issue_type?: AMLIssueType[];
  claimed_by?: string;
  sort_by?: 'detected_at' | 'risk_score' | 'total_amount';
  sort_order?: 'asc' | 'desc';
  date_from?: string;           // YYYY-MM-DD
  date_to?: string;             // YYYY-MM-DD
  show_snoozed?: boolean;
  page?: number;
  page_size?: number;
  signal?: AbortSignal;
}

/**
 * Response for listing AML issues
 */
export interface AMLIssuesResponse {
  entity_id: string;
  issues: AMLIssue[];
  total_count: number;
  filtered_count: number;
  pagination: {
    page: number;
    page_size: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

/**
 * AML Summary Response (for dashboard and summary page)
 */
export interface AMLSummaryResponse {
  entity_id: string;
  total_issues: number;

  by_status: Record<AMLIssueStatus, number>;
  by_type: Record<AMLIssueType, number>;

  requires_action: number;      // new + needs_info + second_review

  // User-specific (requires auth)
  my_claims: number;
  awaiting_my_review: number;
  my_first_reviews: number;

  // Workflow metrics
  avg_time_to_first_review_hours?: number;
  avg_time_to_close_hours?: number;
  resubmit_rate?: number;
}

/**
 * AML Counts Response (for sidebar menu)
 */
export interface AMLCountsResponse {
  entity_id: string;
  total_issues: number;
  issues_by_type: Record<AMLIssueType, number>;
  issues_by_status: Record<AMLIssueStatus, number>;
  action_needed: number;
  date_range: {
    from: string | null;
    to: string | null;
  };
  status_filters: AMLIssueStatus[];
  processing_time_ms: number;
}

/**
 * AML Month Counts Response (for efficient month tabs loading)
 * Replaces loading all issues just to count by month
 */
export interface AMLMonthCountsResponse {
  entity_id: string;
  issue_type: string;
  months: Record<string, number>;  // { "2025-01": 5, "2025-02": 3, ... }
  total: number;
  date_range: {
    from: string | null;
    to: string | null;
  };
  status_filters: string[];
  processing_time_ms: number;
}

/**
 * Claim Issue Request
 */
export interface ClaimIssueRequest {
  issue_hash: string;
}

/**
 * Claim Issue Response
 */
export interface ClaimIssueResponse {
  success: boolean;
  claim_expires_at: string;
  issue_hash: string;
}

/**
 * Release Claim Request
 */
export interface ReleaseClaimRequest {
  issue_hash: string;
}

/**
 * First Review Request
 */
export interface FirstReviewRequest {
  issue_hash: string;
  proposed_outcome: ProposedOutcome;
  reason: string;               // Required, min 3 chars (backend field name)
  version: number;              // Required for optimistic locking
}

/**
 * First Review Response
 */
export interface FirstReviewResponse {
  success: boolean;
  status: 'second_review';
  issue_hash: string;
  version: number;
}

/**
 * Second Review Request
 */
export interface SecondReviewRequest {
  issue_hash: string;
  decision: SecondReviewDecision;
  reason: string;               // Required by backend (min 3 chars)
  version: number;              // Required for optimistic locking
}

/**
 * Second Review Response
 */
export interface SecondReviewResponse {
  success: boolean;
  status: AMLIssueStatus;
  issue_hash: string;
  version: number;
}

/**
 * Add Info Request (after NEEDS_INFO status)
 */
export interface AddInfoRequest {
  issue_hash: string;
  content: string;            // Required, min 3 chars
  version: number;            // Required for optimistic locking
}

/**
 * Add Info Response
 */
export interface AddInfoResponse {
  success: boolean;
  status: 'second_review';    // Returns to second review
  issue_hash: string;
  version: number;
}

/**
 * Add Note Request
 */
export interface AddNoteRequest {
  content: string;
}

/**
 * Add Note Response
 */
export interface AddNoteResponse {
  success: boolean;
  note_id: string;
}

// ============================================================================
// BULK OPERATION TYPES
// ============================================================================

/**
 * Bulk Claim Request
 */
export interface BulkClaimRequest {
  issue_hashes: string[];       // Max 50 per request
}

/**
 * Bulk Claim Response
 */
export interface BulkClaimResponse {
  success_count: number;
  failure_count: number;
  results: Array<{
    issue_hash: string;
    success: boolean;
    error_message?: string;
    claim_expires_at?: string;
  }>;
}

/**
 * Bulk First Review Request
 */
export interface BulkFirstReviewRequest {
  issue_hashes: string[];       // Max 100 per request
  proposed_outcome: ProposedOutcome;
  proposed_reason: string;
}

/**
 * Bulk First Review Response
 */
export interface BulkFirstReviewResponse {
  success_count: number;
  failure_count: number;
  results: Array<{
    issue_hash: string;
    success: boolean;
    error_message?: string;
  }>;
}

/**
 * Bulk Second Review Request
 */
export interface BulkSecondReviewRequest {
  issue_hashes: string[];       // Max 100 per request
  decision: SecondReviewDecision;
  reason?: string;
}

/**
 * Bulk Second Review Response
 */
export interface BulkSecondReviewResponse {
  success_count: number;
  failure_count: number;
  results: Array<{
    issue_hash: string;
    success: boolean;
    error_message?: string;
    new_status?: AMLIssueStatus;
  }>;
}

// ============================================================================
// SNOOZE TYPES
// ============================================================================

/**
 * Snooze Issue Request
 */
export interface SnoozeIssueRequest {
  issue_hash: string;
  snooze_until: string;         // ISO datetime, max 90 days from now
}

/**
 * Bulk Snooze Request
 */
export interface BulkSnoozeRequest {
  issue_hashes: string[];       // Max 500 per request
  snooze_until: string;
}

/**
 * Unsnooze Issue Request
 */
export interface UnsnoozeIssueRequest {
  issue_hash: string;
}

/**
 * Bulk Unsnooze Request
 */
export interface BulkUnsnoozeRequest {
  issue_hashes: string[];       // Max 500 per request
}

// ============================================================================
// ADMIN TYPES
// ============================================================================

/**
 * Reassign Issue Request (firm_admin only)
 */
export interface ReassignIssueRequest {
  issue_hash: string;
  new_reviewer_id: string;
  reason: string;
}

/**
 * Override Separation Request (firm_admin only)
 */
export interface OverrideSeparationRequest {
  issue_hash: string;
  reason: string;
}

// ============================================================================
// ERROR TYPES
// ============================================================================

/**
 * AML Error Response
 */
export interface AMLErrorResponse {
  detail: string;
  error_code?: string;
  issue_hash?: string;
  current_version?: number;     // For optimistic locking conflicts
}

/**
 * AML Count Error (for partial failures)
 */
export interface AMLCountError {
  type: 'timeout' | 'network' | 'permission' | 'unknown';
  message: string;
  retryable: boolean;
  failedTypes?: AMLIssueType[];
}

// ============================================================================
// UI STATE TYPES
// ============================================================================

/**
 * Status badge configuration - Single source of truth for AML status display
 * Used by both AMLIssuesTable and aml.service.ts
 */
export const AML_STATUS_BADGE_CONFIG: Record<AMLIssueStatus, {
  label: string;           // Display text (e.g., "New", "Awaiting 2nd Review")
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  color: string;           // Tailwind CSS classes for styling
  text: string;            // Alias for label (backwards compatibility)
  bgClass: string;         // Alias for color (backwards compatibility)
}> = {
  new: { label: 'New', variant: 'outline', color: 'bg-red-100 text-red-800 border-red-200', text: 'New', bgClass: 'bg-red-100 text-red-800' },
  needs_info: { label: 'Needs Info', variant: 'outline', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', text: 'Needs Info', bgClass: 'bg-yellow-100 text-yellow-800' },
  second_review: { label: 'Awaiting 2nd Review', variant: 'outline', color: 'bg-orange-100 text-orange-800 border-orange-200', text: 'Awaiting 2nd', bgClass: 'bg-orange-100 text-orange-800' },
  closed: { label: 'Closed', variant: 'outline', color: 'bg-gray-100 text-gray-800 border-gray-200', text: 'Closed', bgClass: 'bg-green-100 text-green-800' },
};

/**
 * Statuses that require action (for sidebar counts)
 */
export const AML_STATUS_ACTION_NEEDED: readonly AMLIssueStatus[] = [
  'new',
  'needs_info',
  'second_review',
] as const;

/**
 * Closed/terminal statuses
 */
export const AML_STATUS_CLOSED: readonly AMLIssueStatus[] = [
  'closed',
] as const;

/**
 * AML Filter State (for URL/localStorage)
 */
export interface AMLFilterState {
  status: AMLIssueStatus[];
  issueTypes: AMLIssueType[];
  showSnoozed: boolean;
  sortBy: 'detected_at' | 'risk_score' | 'total_amount';
  sortOrder: 'asc' | 'desc';
  dateFrom?: string;
  dateTo?: string;
}

/**
 * AML Context State
 */
export interface AMLContextState {
  issues: AMLIssue[];
  countsByType: Record<AMLIssueType, number>;
  countsByStatus: Record<AMLIssueStatus, number>;
  totalIssues: number;
  actionNeeded: number;
  isLoading: boolean;
  error: Error | null;
  lastUpdated: Date | null;
}

// =============================================================================
// UI State Types (Added for AML Frontend Implementation)
// =============================================================================

/** Modal state for AML detail page */
export type AMLModalType = 'claim' | 'firstReview' | 'secondReview' | 'snooze' | 'bulkAction' | 'notes' | null;

/** Bulk action types available in AML */
export type AMLBulkActionType = 'claim' | 'firstReview' | 'snooze' | 'unsnooze';

/** Selection state for table */
export interface AMLSelectionState {
  selectedIds: Set<string>;
  lastSelectedId: string | null;
  anchorId: string | null;
}

/** Sort configuration for AML table */
export interface AMLSortConfig {
  field: 'detected_at' | 'risk_score' | 'amount' | 'status' | 'type';
  direction: 'asc' | 'desc';
}

/** Claim expiry info for UI display */
export interface ClaimExpiryInfo {
  expiresAt: string;
  hoursRemaining: number;
  isExpiringSoon: boolean; // < 2 hours
  isExpired: boolean;
}

// ============================================================================
// UI CONSTANTS
// ============================================================================

/**
 * Badge configuration for review actions in activity timeline
 */
export const AML_REVIEW_ACTION_BADGE_CONFIG: Record<string, { label: string; className: string }> = {
  claim: { label: 'Claimed', className: 'bg-blue-100 text-blue-800' },
  release: { label: 'Released', className: 'bg-gray-100 text-gray-800' },
  first_review: { label: 'First Review', className: 'bg-orange-100 text-orange-800' },
  second_review: { label: 'Second Review', className: 'bg-green-100 text-green-800' },
  add_info: { label: 'Info Added', className: 'bg-purple-100 text-purple-800' },
  comment: { label: 'Comment', className: 'bg-gray-100 text-gray-800' },
  snooze: { label: 'Snoozed', className: 'bg-yellow-100 text-yellow-800' },
  unsnooze: { label: 'Unsnoozed', className: 'bg-yellow-100 text-yellow-800' },
};
